import { isGenericYouTubeLabel, parseYouTubeVideoId } from './youtubeUrl.js';

/** Context-menu / dialog title for a classified clipboard payload. */
export const CLIPBOARD_PASTE_FALLBACK_LABEL = 'Paste clipboard';

/** Descriptive bit shown after "Paste". The menu grows to fit this. */
const PREVIEW_LIMIT = 52;

const LABELS = {
  image: 'Paste image',
  youtube: 'Paste YouTube video',
  url: 'Paste URL',
  text: 'Paste text',
};

const GENERIC_IMAGE_LABEL = /^(image|img|photo|picture|screenshot|clipboard|untitled)(\.(png|jpe?g|gif|webp|bmp|svg))?$/i;

/**
 * @param {'image'|'youtube'|'url'|'text'|null|undefined} kind
 * @returns {string}
 */
export function labelForClipboardKind(kind) {
  return LABELS[kind] || CLIPBOARD_PASTE_FALLBACK_LABEL;
}

/**
 * Context-menu label: the paste action plus a short look at what is copied.
 * Text is quoted, a link prefers a title then the url, YouTube prefers a
 * title then the video id, and an image prefers alt text or a short caption.
 * @param {{ kind?: string|null, text?: string, html?: string }} payload
 * @returns {string}
 */
export function labelForClipboardContents({ kind, text = '', html = '' } = {}) {
  if (kind === 'text') {
    const preview = textPreview(text);
    return preview ? `Paste “${preview}”` : labelForClipboardKind('text');
  }
  if (kind === 'url') {
    const preview = urlPreview(text, html);
    return preview ? `Paste ${preview}` : labelForClipboardKind('url');
  }
  if (kind === 'youtube') {
    const preview = youtubePreview(text, html);
    if (!preview) return labelForClipboardKind('youtube');
    if (preview.startsWith('youtu.be/')) return `Paste ${preview}`;
    return `Paste YouTube · ${preview}`;
  }
  if (kind === 'image') {
    const preview = imagePreview(text, html);
    return preview ? `Paste image · ${preview}` : labelForClipboardKind('image');
  }
  return labelForClipboardKind(kind);
}

function textPreview(text) {
  const lines = textLines(text);
  if (!lines.length) return '';
  let body = lines[0];
  if (Array.from(body).length < 8 && lines[1]) body = `${body} — ${lines[1]}`;
  return clip(body, PREVIEW_LIMIT);
}

function urlPreview(text, html) {
  const raw = String(text || '').trim();
  const parsed = parseHttpUrl(raw);
  if (!parsed) return clip(raw, PREVIEW_LIMIT);
  const host = bareHost(parsed);
  const title = firstUsefulTitle([
    anchorLabel(html),
    metaContent(html, ['og:title', 'twitter:title']),
    titleTag(html),
    pathSlugTitle(parsed.pathname),
    searchQueryTitle(parsed),
  ], raw);
  if (title) return clip(`${host} · ${title}`, PREVIEW_LIMIT);
  return clip(compactUrl(parsed), PREVIEW_LIMIT);
}

function youtubePreview(text, html) {
  const raw = String(text || '').trim();
  const title = firstUsefulTitle([
    anchorLabel(html),
    metaContent(html, ['og:title', 'twitter:title']),
    titleTag(html),
  ], raw);
  if (title) return clip(title, PREVIEW_LIMIT);
  const id = parseYouTubeVideoId(raw);
  if (id) return `youtu.be/${id}`;
  return clip(String(raw).replace(/^https?:\/\//i, ''), PREVIEW_LIMIT);
}

function imagePreview(text, html) {
  const alt = usefulTitle(imgAlt(html));
  if (alt && !GENERIC_IMAGE_LABEL.test(alt)) return clip(alt, PREVIEW_LIMIT);
  const lines = textLines(text);
  const caption = lines[0] || '';
  if (!caption || looksLikeHttpUrl(caption) || GENERIC_IMAGE_LABEL.test(caption)) return '';
  if (Array.from(caption).length > 60) return '';
  return clip(caption, PREVIEW_LIMIT);
}

function firstUsefulTitle(candidates, url) {
  for (const candidate of candidates) {
    const title = usefulTitle(candidate, url);
    if (title) return title;
  }
  return '';
}

function usefulTitle(value, url = '') {
  const text = clean(decodeBasicEntities(value));
  if (Array.from(text).length < 2) return '';
  if (looksLikeHttpUrl(text)) return '';
  if (isGenericYouTubeLabel(text)) return '';
  const videoId = parseYouTubeVideoId(url);
  if (videoId && (text === videoId || text === `youtu.be/${videoId}`)) return '';
  if (url && text === String(url).trim()) return '';
  return text;
}

function textLines(value) {
  return String(value || '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map(line => line.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean);
}

function clean(value) {
  return textLines(value).join(' ');
}

function clip(value, limit) {
  const flat = clean(value);
  const chars = Array.from(flat);
  if (chars.length <= limit) return flat;
  return `${chars.slice(0, Math.max(1, limit - 1)).join('').trimEnd()}…`;
}

function looksLikeHttpUrl(value) {
  return Boolean(parseHttpUrl(value));
}

function parseHttpUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed;
  } catch {
    return null;
  }
}

function bareHost(parsed) {
  return String(parsed.hostname || '').replace(/^www\./i, '');
}

function compactUrl(parsed) {
  const host = bareHost(parsed);
  let path = parsed.pathname || '';
  try {
    path = decodeURIComponent(path);
  } catch {
    /* keep the raw path */
  }
  if (path.length > 1) path = path.replace(/\/+$/, '');
  if (path === '/') path = '';
  return `${host}${path}`;
}

function pathSlugTitle(pathname) {
  let path = String(pathname || '');
  try {
    path = decodeURIComponent(path);
  } catch {
    /* keep the raw path */
  }
  const parts = path.split('/').filter(Boolean);
  if (!parts.length) return '';
  const last = parts[parts.length - 1].replace(/\.(html?|php|aspx?)$/i, '');
  if (!/[a-z]/i.test(last)) return '';
  if (/^[a-f0-9-]{16,}$/i.test(last)) return '';
  const words = last.split(/[-_+]+/).map(word => word.trim()).filter(word => word && !/^\d+$/.test(word));
  if (words.length < 2) return '';
  return words.join(' ');
}

function searchQueryTitle(parsed) {
  return parsed.searchParams?.get?.('q') || parsed.searchParams?.get?.('query') || '';
}

function htmlHead(html) {
  return String(html || '').slice(0, 8000);
}

function anchorLabel(html) {
  const match = htmlHead(html).match(/<a\b[^>]*>([\s\S]*?)<\/a>/i);
  if (!match) return '';
  return match[1].replace(/<[^>]+>/g, ' ');
}

function titleTag(html) {
  const match = htmlHead(html).match(/<title\b[^>]*>([\s\S]*?)<\/title>/i);
  if (!match) return '';
  return match[1].replace(/<[^>]+>/g, ' ');
}

function imgAlt(html) {
  const match = htmlHead(html).match(/<img\b[^>]*\balt\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
  return match?.[1] || match?.[2] || '';
}

function metaContent(html, names) {
  const source = htmlHead(html);
  const keys = names.map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const patterns = [
    new RegExp(`<meta\\b[^>]*\\b(?:property|name)\\s*=\\s*["'](?:${keys})["'][^>]*\\bcontent\\s*=\\s*["']([^"']*)["']`, 'i'),
    new RegExp(`<meta\\b[^>]*\\bcontent\\s*=\\s*["']([^"']*)["'][^>]*\\b(?:property|name)\\s*=\\s*["'](?:${keys})["']`, 'i'),
  ];
  for (const pattern of patterns) {
    const match = source.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
}

function decodeBasicEntities(value) {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, codePoint) => fromCodePoint(codePoint, 10))
    .replace(/&#x([0-9a-f]+);/gi, (_, codePoint) => fromCodePoint(codePoint, 16));
}

function fromCodePoint(raw, radix) {
  const code = Number.parseInt(raw, radix);
  if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}
